/**
 * Login Store - State management for Login.tsx page
 *
 * Related page: frontend/src/pages/Login.tsx
 *
 * This store demonstrates the "form with validation" pattern:
 * - loginFormAtom: form data (email, password, isSubmitting)
 * - loginFormErrorsAtom: null before first submit, then contains errors
 * - Once submitted, errors update in real-time as user types
 */

import { atom } from 'jotai'
import { authClient } from '../client'
import { FormError } from 'txom-frontend-libs'
import { getAuthStateChangedHandler$ } from 'txom-frontend-libs'
import { resetQueryClient } from '../../queryClient'

// ============================================================================
// FORM STATE
// ============================================================================

const initialLoginForm = {
  email: '',
  password: '',
  isSubmitting: false,
}

type LoginForm = typeof initialLoginForm

export const loginFormAtom = atom(initialLoginForm)

// ============================================================================
// FORM ERRORS
// - null: form has never been submitted, don't show errors
// - object: form has been submitted, show errors and validate in real-time
// ============================================================================

type LoginFormErrors = {
  form?: string // General form-level error (e.g., "Invalid credentials")
  email?: string
  password?: string
}

export const loginFormErrorsAtom = atom<LoginFormErrors | null>(null)

// ============================================================================
// VALIDATION
// ============================================================================

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function hasFieldErrors(errors: LoginFormErrors): boolean {
  const { form: _form, ...fieldErrors } = errors
  return Object.keys(fieldErrors).length > 0
}

const validateLoginFormAtom = atom(null, (get, set) => {
  const form = get(loginFormAtom)
  const errors: LoginFormErrors = {}

  if (!form.email.trim()) {
    errors.email = 'Email is required'
  } else if (!isValidEmail(form.email)) {
    errors.email = 'Invalid email format'
  }

  if (!form.password) {
    errors.password = 'Password is required'
  } else if (form.password.length < 8) {
    errors.password = 'Password must be at least 8 characters'
  }

  set(loginFormErrorsAtom, errors)
  return hasFieldErrors(errors)
})

const setLoginFormErrorAtom = atom(null, (get, set, err: unknown, fallback: string) => {
  const message = err instanceof FormError ? err.message : (err instanceof Error ? err.message || fallback : fallback)
  set(loginFormErrorsAtom, (current) => ({ ...(current ?? {}), form: message }))
})

// ============================================================================
// FORM ACTIONS
// ============================================================================

export const updateLoginFormAtom = atom(null, (get, set, updates: Partial<LoginForm>) => {
  const current = get(loginFormAtom)
  set(loginFormAtom, { ...current, ...updates })

  // If form has been submitted before (errors !== null), validate in real-time
  if (get(loginFormErrorsAtom) !== null) {
    set(validateLoginFormAtom)
  }
})

export const submitLoginAtom = atom(null, async (get, set) => {
  const form = get(loginFormAtom)

  // Validate and enable real-time validation mode
  if (set(validateLoginFormAtom)) {
    throw new FormError('Please fix the errors above')
  }

  set(loginFormAtom, { ...form, isSubmitting: true })

  try {
    const res = await authClient.signIn.email({
      email: form.email,
      password: form.password,
    })
    if (res.error) {
      throw new FormError(res.error.message || 'Sign in failed')
    }

    const handler = get(getAuthStateChangedHandler$)
    if (handler) {
      handler(true)
    }

    // Reset jotai-tanstack-query state to trigger refetch
    set(resetQueryClient)
  } catch (err) {
    set(setLoginFormErrorAtom, err, 'Sign in failed')
    throw err
  } finally {
    set(loginFormAtom, (current) => ({ ...current, isSubmitting: false }))
  }
})

// OAuth sign in action atom
export const oAuthSignInAtom = atom(null, async (get, set) => {
  try {
    await authClient.signIn.social({
      provider: 'google',
      callbackURL: window.location.origin + '/auth/callback',
    })

    const handler = get(getAuthStateChangedHandler$)
    if (handler) {
      handler(true)
    }
  } catch (err) {
    set(setLoginFormErrorAtom, err, 'OAuth sign in failed')
    throw err
  }
})

// ============================================================================
// ROUTE LOADERS
// ============================================================================

type Store = ReturnType<typeof import('jotai').createStore>

export async function loader(_store: Store) {
  return null
}
